import { useState, useEffect, useMemo } from "react";
import { Field, Toggle } from "./components.js";
import {
  createCustomer,
  getAllCustomers,
  updateCustomer,
  permanentlyDeleteCustomer,
} from "./db/index.js";


// ══════════════════════════════════════════════════════════════════════════════
// SESSION STORAGE OPERATIONS (FOR FREE TIER)
// ══════════════════════════════════════════════════════════════════════════════

const SESSION_STORAGE_KEY = "invoice_gen_customers_session";

/**
 * Get customers from sessionStorage (FREE tier only)
 * @returns {Array} Array of customers
 */
const getCustomersFromSession = () => {
  try {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Failed to load customers from sessionStorage:", error);
    return [];
  }
};

/**
 * Save customers to sessionStorage (FREE tier only)
 * @param {Array} customers - Array of customers to save
 */
const saveCustomersToSession = (customers) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(customers));
  } catch (error) {
    console.error("Failed to save customers to sessionStorage:", error);
    throw error;
  }
};

/**
 * Add new customer to sessionStorage (FREE tier only)
 * @param {object} customerData - Customer data to add
 * @returns {object} Created customer with generated ID
 */
const addCustomerToSession = (customerData) => {
  const customers = getCustomersFromSession();
  const newCustomer = {
    ...customerData,
    id: `cust_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    lastUsedAt: null,
    invoiceCount: 0,
    archived: false
  };
  customers.push(newCustomer);
  saveCustomersToSession(customers);
  return newCustomer;
};

/**
 * Update customer in sessionStorage (FREE tier only)
 * @param {string} customerId - Customer ID to update
 * @param {object} updates - Fields to update
 * @returns {object|null} Updated customer or null if not found
 */
const updateCustomerInSession = (customerId, updates) => {
  const customers = getCustomersFromSession();
  const index = customers.findIndex(c => c.id === customerId);
  if (index !== -1) {
    customers[index] = {
      ...customers[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    saveCustomersToSession(customers);
    return customers[index];
  }
  return null;
};

/**
 * Delete customer from sessionStorage (FREE tier only)
 * @param {string} customerId - Customer ID to delete
 */
const deleteCustomerFromSession = (customerId) => {
  const customers = getCustomersFromSession();
  const filtered = customers.filter(c => c.id !== customerId);
  saveCustomersToSession(filtered);
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════


export default function Customers({ G, brand, settings, user }) {
  const empty = {
    name: "",
    email: "",
    phone: "",
    gstin: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    zip: ""
  };

  const darkMode = G.bg === "#1C1C1E";
 
  // Modal & Form State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [modalForm, setModalForm] = useState(empty);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [clearNext, setClearNext] = useState(false);
  const [btnPulse, setBtnPulse] = useState(false);
  const [saving, setSaving] = useState(false);

  // Customer list
  const [customers, setCustomers] = useState([]);

  // Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");


  // ══════════════════════════════════════════════════════════════════════════════
  // LOAD CUSTOMERS ON MOUNT (TIER-BASED)
  // ══════════════════════════════════════════════════════════════════════════════


  useEffect(() => {
    const loadCustomers = async () => {
      try {
        if (user.tierLevel === 0) {
          // ═══════════════════════════════════════════════════════════════════
          // FREE TIER - Load from sessionStorage
          // ═══════════════════════════════════════════════════════════════════
          const sessionCustomers = getCustomersFromSession();
          setCustomers(sessionCustomers);
          
        } else {
          // ═══════════════════════════════════════════════════════════════════
          // PAID TIER - Load from database
          // ═══════════════════════════════════════════════════════════════════
          const allCustomers = await getAllCustomers();
          setCustomers(allCustomers);
        }
      } catch (error) {
        console.error("Failed to load customers:", error);
        alert("Failed to load customers. Please refresh the page.");
      }
    };
   
    loadCustomers();
  }, [user.tierLevel]);


  // ══════════════════════════════════════════════════════════════════════════════
  // MODAL HANDLERS
  // ══════════════════════════════════════════════════════════════════════════════


  const openModal = (mode, customer = null) => {
    setModalMode(mode);
    setClearNext(false);
   
    if (mode === "edit" && customer) {
      setEditingCustomer(customer);
      setModalForm({
        name: customer.name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        gstin: customer.gstin || "",
        addressLine1: customer.addressLine1 || "",
        addressLine2: customer.addressLine2 || "",
        city: customer.city || "",
        state: customer.state || "",
        country: customer.country || "",
        zip: customer.zip || ""
      });
    } else {
      setEditingCustomer(null);
      setModalForm(empty);
    }
   
    setShowModal(true);
  };


  const closeModal = () => {
    setShowModal(false);
    setClearNext(false);
    setSaving(false);
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // SAVE HANDLER (CREATE OR UPDATE) - TIER-BASED WITH LIMIT CHECK
  // ══════════════════════════════════════════════════════════════════════════════


  const handleSaveModal = async () => {
    // ────────────────────────────────────────────────────────────────────────
    // STEP 1: VALIDATE FORM DATA
    // ────────────────────────────────────────────────────────────────────────
    
    if (!modalForm.name || !modalForm.name.trim()) {
      alert("Customer name is required.");
      return;
    }

    // ────────────────────────────────────────────────────────────────────────
    // STEP 2: CHECK CUSTOMER LIMIT (Only for new customers)
    // ────────────────────────────────────────────────────────────────────────
    
if (modalMode === "add") {
  const currentCount = customers.length;
  const limit = user.usage?.customerLimit || (user.tierLevel === 0 ? 5 : null);
  
  // Only check limit if it's defined (null = unlimited)
  if (limit !== null && currentCount >= limit) {
        alert(
          `Customer Limit Reached!\n\n` +
          `Your ${user.tier} tier allows ${limit} customer${limit !== 1 ? 's' : ''}.\n\n` +
          `Upgrade to add more customers.`
        );
        return;
      }
    }

    setSaving(true);
    setBtnPulse(true);

    try {
      const customerData = {
        name: modalForm.name.trim(),
        email: modalForm.email.trim(),
        phone: modalForm.phone.trim(),
        gstin: modalForm.gstin.trim(),
        addressLine1: modalForm.addressLine1.trim(),
        addressLine2: modalForm.addressLine2.trim(),
        city: modalForm.city.trim(),
        state: modalForm.state.trim(),
        country: modalForm.country.trim(),
        zip: modalForm.zip.trim()
      };

      if (user.tierLevel === 0) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - SessionStorage Path (No Database)
        // ═══════════════════════════════════════════════════════════════════
        
        if (modalMode === "edit" && editingCustomer) {
          // Update existing customer in sessionStorage
          const updated = updateCustomerInSession(editingCustomer.id, customerData);
          if (!updated) {
            throw new Error("Failed to update customer in session storage");
          }
        } else {
          // Create new customer in sessionStorage
          addCustomerToSession(customerData);
        }

        // Reload from sessionStorage
        const freshCustomers = getCustomersFromSession();
        setCustomers(freshCustomers);

      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Database Path
        // ═══════════════════════════════════════════════════════════════════
        
        if (modalMode === "edit" && editingCustomer) {
          // UPDATE existing customer in database
          await updateCustomer(editingCustomer.id, customerData);
        } else {
          // CREATE new customer in database
          await createCustomer(customerData);
        }

        // Reload customers from DB
        const freshCustomers = await getAllCustomers();
        setCustomers(freshCustomers);
      }

      // Keep modal open until animation finishes
      setTimeout(() => {
        setBtnPulse(false);
        setSaving(false);
       
        if (clearNext && modalMode === "add") {
          // Clear form for next entry
          setModalForm(empty);
        } else {
          // Close modal
          closeModal();
        }
      }, 450);


    } catch (error) {
      console.error("Failed to save customer:", error);
      
      // ✅ IMPROVED: Better error messaging with details
      let errorMessage = "Failed to save customer. Please try again.";
      
      if (error.name === 'ValidationError') {
        errorMessage = `Validation Error: ${error.message}`;
      } else if (error.name === 'PermissionDeniedError') {
        errorMessage = "Permission denied. Upgrade required for this feature.";
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
      setBtnPulse(false);
      setSaving(false);
    }
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // DELETE HANDLER (PERMANENT - NO RECOVERY) - TIER-BASED
  // ══════════════════════════════════════════════════════════════════════════════


  const handleDelete = async (customer) => {
    if (!window.confirm(
      `⚠️ Permanently Delete "${customer.name}"?\n\n` +
      `This will permanently remove this customer from your records.\n\n` +
      `⚠️ This action CANNOT be undone.\n\n` +
      `Note: To delete all customers with 30-day recovery, use "Delete Customer Data" in Settings.\n\n` +
      `Continue with permanent deletion?`
    )) {
      return;
    }


    try {
      if (user.tierLevel === 0) {
        // ═══════════════════════════════════════════════════════════════════
        // FREE TIER - Delete from sessionStorage
        // ═══════════════════════════════════════════════════════════════════
        
        deleteCustomerFromSession(customer.id);
        
        // Reload from sessionStorage
        const freshCustomers = getCustomersFromSession();
        setCustomers(freshCustomers);
        
      } else {
        // ═══════════════════════════════════════════════════════════════════
        // PAID TIER - Permanent delete from database
        // ═══════════════════════════════════════════════════════════════════
        
        await permanentlyDeleteCustomer(customer.id);
        
        // Reload customers from DB
        const freshCustomers = await getAllCustomers();
        setCustomers(freshCustomers);
      }
      
    } catch (error) {
      console.error("Failed to delete customer:", error);
      
      // ✅ IMPROVED: Better error messaging
      let errorMessage = "Failed to delete customer. Please try again.";
      
      if (error.name === 'PermissionDeniedError') {
        errorMessage = "Permission denied. You don't have access to delete customers.";
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };


  // ══════════════════════════════════════════════════════════════════════════════
  // SORTING
  // ══════════════════════════════════════════════════════════════════════════════


  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };
const SortIcon = ({ column }) => {
  if (sortColumn !== column) {
    return <span style={{ color: G.ink, fontSize: "14px", marginLeft: "4px", opacity: 0.4, fontWeight: 600 }}>⇅</span>;
  }
  return sortDirection === "asc"
    ? <span style={{ color: G.ink, fontSize: "14px", marginLeft: "4px", fontWeight: 700 }}>▲</span>
    : <span style={{ color: G.ink, fontSize: "14px", marginLeft: "4px", fontWeight: 700 }}>▼</span>;
};

  // ══════════════════════════════════════════════════════════════════════════════
  // FILTERING & SORTING
  // ══════════════════════════════════════════════════════════════════════════════


  const processedCustomers = useMemo(() => {
    let filtered = customers.filter(c => {
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesText =
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.phone || "").toLowerCase().includes(q) ||
          (c.city || "").toLowerCase().includes(q) ||
          (c.gstin || "").toLowerCase().includes(q) ||
          (c.state || "").toLowerCase().includes(q) ||
          (c.country || "").toLowerCase().includes(q);
        if (!matchesText) return false;
      }
     
      return true;
    });


    // Sort
    return filtered.sort((a, b) => {
      let valA, valB;
     
      switch (sortColumn) {
        case "name":
          valA = (a.name || "").toLowerCase();
          valB = (b.name || "").toLowerCase();
          break;
        case "email":
          valA = (a.email || "").toLowerCase();
          valB = (b.email || "").toLowerCase();
          break;
        case "phone":
          valA = (a.phone || "").toLowerCase();
          valB = (b.phone || "").toLowerCase();
          break;
        case "city":
          valA = (a.city || "").toLowerCase();
          valB = (b.city || "").toLowerCase();
          break;
        case "gstin":
          valA = (a.gstin || "").toLowerCase();
          valB = (b.gstin || "").toLowerCase();
          break;
        default:
          return 0;
      }
     
      valA = String(valA);
      valB = String(valB);
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [customers, searchQuery, sortColumn, sortDirection]);


  // ══════════════════════════════════════════════════════════════════════════════
  // TABLE COLUMNS
  // ══════════════════════════════════════════════════════════════════════════════

  //Change column width here
const tableColumns = [
  { key: "name", label: "Name", sortable: true, width: "25%" },
  { key: "email", label: "Email", sortable: true, width: "25%" },
  { key: "phone", label: "Phone", sortable: true, width: "15%" },
  { key: "gstin", label: "Tax ID", sortable: true, width: "12%" },
  { key: "city", label: "City", sortable: true, width: "12%" },
  { key: "actions", label: "Actions", sortable: false, width: "11%" }
];
  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════════


  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
      {/* Header Row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, color: G.ink, marginBottom: 4 }}>
            👥 Customers
          </h2>
<div style={{ color: G.muted, fontSize: 13 }}>
  {customers.length} customer{customers.length !== 1 ? "s" : ""} total
  {user.tierLevel === 0 ? (
    <span style={{ color: G.warning, fontWeight: 500 }}>
      {" "}• {customers.length}/5 used
    </span>
  ) : user.usage?.customerLimit ? (
    <span style={{ color: G.accent, fontWeight: 500 }}>
      {" "}• Limit: {user.usage.customerLimit}
    </span>
  ) : (
    <span style={{ color: G.success, fontWeight: 500 }}>
      {" "}• Unlimited
    </span>
  )}
</div>
        </div>
 <button
  onClick={() => openModal("add")}
  style={{
    background: darkMode ? G.surface2 : G.ink,
    color: darkMode ? G.ink : G.surface,
    border: darkMode ? `2px solid ${G.border}` : "none",
    borderRadius: 7,
    padding: "7px 15px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Sora', sans-serif",
    display: "flex",
    alignItems: "center",
    gap: 6,
    boxShadow: G.shadow
  }}
>
          ➕ Add Customer
        </button>
      </div>

      {/* Filter Row */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          background: G.surface,
          border: `1.5px solid ${G.border}`,
          borderRadius: 9,
          padding: "10px 14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
        }}>
          <label style={{
            fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 4, marginBottom: 6
          }}>
            <span style={{ fontSize: 12 }}>🔍</span> Search
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name / Email / Phone / City / GSTIN"
              maxLength={100}
              style={{
  flex: 1, padding: "8px 10px", border: `1.5px solid ${G.border}`, borderRadius: 6,
  fontSize: 13, fontFamily: "'Sora', sans-serif", outline: "none", color: G.ink,
  background: G.surface, transition: "border-color 0.15s"
}}
              onFocus={e => e.target.style.borderColor = G.accent}
              onBlur={e => e.target.style.borderColor = G.border}
            />
            <button
              disabled={!searchQuery}
              onClick={() => setSearchQuery("")}
              style={{
                background: searchQuery ? G.accentBg : G.surface2,
                color: searchQuery ? G.accent : G.muted,
                border: `1.5px solid ${searchQuery ? G.accent : G.border}40`,
                borderRadius: 6, padding: "5px 12px", fontSize: 12,
                cursor: searchQuery ? "pointer" : "not-allowed",
                fontFamily: "'Sora', sans-serif", fontWeight: 500, whiteSpace: "nowrap",
                transition: "all 0.15s", flexShrink: 0
              }}
              onMouseEnter={e => {
                if (searchQuery) {
                  e.target.style.background = G.accent;
                  e.target.style.color = G.accentFg;
                  e.target.style.border = "1.5px solid transparent";
                }
              }}
              onMouseLeave={e => {
                e.target.style.background = searchQuery ? G.accentBg : G.surface2;
                e.target.style.color = searchQuery ? G.accent : G.muted;
                e.target.style.border = `1.5px solid ${searchQuery ? G.accent : G.border}40`;
              }}
            >
              Clear
            </button>
          </div>
          <div style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>{searchQuery.length}/100 characters</div>
        </div>
      </div>


      {/* Customers Table */}
      <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, overflow: "auto", marginBottom: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr style={{ background: G.surface2 }}>
              {tableColumns.map(col => (
                <th
  key={col.key}
  onClick={() => col.sortable && handleSort(col.key)}
  onMouseEnter={col.sortable ? e => e.target.style.background = "rgba(0,0,0,0.05)" : undefined}
onMouseLeave={col.sortable ? e => e.target.style.background = "transparent" : undefined}
style={{
  color: G.ink,
    padding: "10px 14px",
    textAlign: col.key === "actions" ? "center" : "left",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: .6,
    textTransform: "uppercase",
    cursor: col.sortable ? "pointer" : "default",
    userSelect: col.sortable ? "none" : "auto",
    transition: "background 0.15s",
    whiteSpace: "nowrap",
    width: col.width  // ✅ ADDED
  }}
>
                  {col.label} {col.sortable && <SortIcon column={col.key} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedCustomers.length === 0 ? (
              <tr>
                <td colSpan={tableColumns.length} style={{ padding: "40px 20px", textAlign: "center", color: G.muted, fontSize: 13 }}>
                  No customers match your filters
                </td>
              </tr>
            ) : (
              processedCustomers.map((c, i) => (
                <tr
                  key={c.id}
                  style={{
                    borderTop: `1px solid ${G.border}`,
                    background: i % 2 === 0 ? G.surface : G.surface2,
                    transition: "background .1s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = G.accentBg}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? G.surface : G.surface2}
                >
<td style={{ padding: "12px 14px", fontWeight: 500, width: "25%" }}>{c.name}</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, width: "25%" }}>
  {c.email || "—"}
</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, fontFamily: "monospace", width: "15%" }}>
  {c.phone || "—"}
</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, fontFamily: "monospace", width: "12%" }}>
  {c.gstin || "—"}
</td>
<td style={{ padding: "12px 14px", color: G.muted, fontSize: 12, width: "12%" }}>
  {c.city || "—"}
</td>

<td style={{ padding: "12px 14px", width: "11%" }}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                      <button
                        onClick={() => openModal("edit", c)}
                        style={{
                          background: G.accentBg,
                          color: G.accent,
                          border: `1.5px solid ${G.accent}40`,
                          borderRadius: 6,
                          padding: "5px 12px",
                          fontSize: 12,
                          cursor: "pointer",
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          transition: "all 0.15s",
                          minWidth: 48,
                          textAlign: "center"
                        }}
                        onMouseEnter={e => {
                          e.target.style.background = G.accent;
                          e.target.style.color = G.accentFg;
                          e.target.style.border = "1.5px solid transparent";
                        }}
                        onMouseLeave={e => {
                          e.target.style.background = G.accentBg;
                          e.target.style.color = G.accent;
                          e.target.style.border = `1.5px solid ${G.accent}40`;
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(c)}
                        style={{
                          background: G.dangerBg,
                          color: G.danger,
                          border: `1.5px solid ${G.danger}40`,
                          borderRadius: 6,
                          padding: "5px 12px",
                          fontSize: 12,
                          cursor: "pointer",
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          transition: "all 0.15s",
                          minWidth: 48,
                          textAlign: "center"
                        }}
                        onMouseEnter={e => {
                          e.target.style.background = G.danger;
                          e.target.style.color = "#FFFFFF";
                          e.target.style.border = "1.5px solid transparent";
                        }}
                        onMouseLeave={e => {
                          e.target.style.background = G.dangerBg;
                          e.target.style.color = G.danger;
                          e.target.style.border = `1.5px solid ${G.danger}40`;
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>


      {/* Add / Edit Modal */}
      {showModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999, padding: "20px"
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "28px",
            maxWidth: 960, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            maxHeight: "90vh", overflowY: "auto", border: `1px solid ${G.border}`
          }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16, color: G.ink }}>
              {modalMode === "edit" ? "✏️ Edit Customer" : "➕ Add Customer"}
            </div>


            {/* Basic Information */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: G.ink, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${G.border}` }}>
                Basic Information
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <Field label="Customer Name*">
                  <input
                    value={modalForm.name}
                    onChange={e => setModalForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Enter customer name"
                    maxLength={100}
                    autoFocus
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {modalForm.name.length}/100 characters
                  </div>
                </Field>

                <Field label="GSTIN / Tax ID">
                  <input
                    value={modalForm.gstin}
                    onChange={e => setModalForm(p => ({ ...p, gstin: e.target.value }))}
                    placeholder="Enter tax ID"
                    maxLength={30}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.gstin || "").length}/30 characters
                  </div>
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Email">
                  <input
                    type="email"
                    value={modalForm.email}
                    onChange={e => setModalForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="customer@example.com"
                    maxLength={100}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.email || "").length}/100 characters
                  </div>
                </Field>

                <Field label="Phone">
                  <input
                    type="tel"
                    value={modalForm.phone}
                    onChange={e => setModalForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                    maxLength={20}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.phone || "").length}/20 characters
                  </div>
                </Field>
              </div>
            </div>

            {/* Address Information */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: G.ink, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${G.border}` }}>
                Address (Optional)
              </div>

              <div style={{ marginBottom: 12 }}>
                <Field label="Address Line 1">
                  <input
                    value={modalForm.addressLine1}
                    onChange={e => setModalForm(p => ({ ...p, addressLine1: e.target.value }))}
                    placeholder="Street address, P.O. box"
                    maxLength={100}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.addressLine1 || "").length}/100 characters
                  </div>
                </Field>
              </div>

              <div style={{ marginBottom: 12 }}>
                <Field label="Address Line 2">
                  <input
                    value={modalForm.addressLine2}
                    onChange={e => setModalForm(p => ({ ...p, addressLine2: e.target.value }))}
                    placeholder="Apartment, suite, unit, building, floor"
                    maxLength={100}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.addressLine2 || "").length}/100 characters
                  </div>
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <Field label="City">
                  <input
                    value={modalForm.city}
                    onChange={e => setModalForm(p => ({ ...p, city: e.target.value }))}
                    placeholder="City"
                    maxLength={50}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.city || "").length}/50 characters
                  </div>
                </Field>

                <Field label="State / Province">
                  <input
                    value={modalForm.state}
                    onChange={e => setModalForm(p => ({ ...p, state: e.target.value }))}
                    placeholder="State or Province"
                    maxLength={50}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.state || "").length}/50 characters
                  </div>
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Country">
                  <input
                    value={modalForm.country}
                    onChange={e => setModalForm(p => ({ ...p, country: e.target.value }))}
                    placeholder="Country"
                    maxLength={50}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.country || "").length}/50 characters
                  </div>
                </Field>

                <Field label="ZIP / Postal Code">
                  <input
                    value={modalForm.zip}
                    onChange={e => setModalForm(p => ({ ...p, zip: e.target.value }))}
                    placeholder="ZIP or Postal Code"
                    maxLength={15}
                  />
                  <div style={{ fontSize: 10, color: G.muted, marginTop: 3 }}>
                    {(modalForm.zip || "").length}/15 characters
                  </div>
                </Field>
              </div>
            </div>


            {/* Modal Actions */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 20,
              marginTop: 24,
              paddingTop: 16,
              borderTop: `1px solid ${G.border}`
            }}>
              {modalMode === "add" && (
                <div style={{ marginInlineEnd: "auto" }}>
                  <Toggle G={G} label="Keep Form Open" value={clearNext} onChange={setClearNext} />
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={closeModal}
                  disabled={saving}
                  style={{
                    padding: "8px 16px",
                    border: `1px solid ${G.border}`,
                    borderRadius: 7,
                    background: G.surface2,
                    color: G.ink,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    opacity: saving ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveModal}
                  disabled={saving}
                  className={btnPulse ? "btn-pulse" : ""}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: 7,
                    background: saving ? G.muted : G.ink,
                    color: G.surface,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  {saving ? "Saving..." : (modalMode === "edit" ? "Update Customer" : "Save Customer")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}